import { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api from '@/lib/api'
import { formatDate, cn } from '@/lib/utils'
import { MetricCard, LoadingSpinner } from '@/components/ui'
import { RefreshCw, Building2, UserCheck, UserMinus, UserPlus, X, Grid3X3, List, ChevronDown, ChevronRight, Crown } from 'lucide-react'

interface Person {
  name: string
  position: string
  manager: string
  status: 'Present' | 'Left'
  date_joined: string | null
  date_left: string | null
  tenure: string | null
  sort_by: string
  tagging: string
  location: string
  is_main_man: boolean
  csuite_roles: string[]
}

interface CoverageInfo {
  coverage: string
  source: string
  freshness: string
}

const CSUITE_ROLES = ['CEO', 'CIO', 'CFO', 'COO', 'CTO', 'CRO', 'CCO'] as const

function coverageBadge(level: string) {
  const l = (level || '').toLowerCase()
  if (l.includes('full')) return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Full Coverage</span>
  if (l.includes('key')) return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">Key Persons Only</span>
  if (l.includes('no')) return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">No Coverage</span>
  return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">{level}</span>
}

function statusBadge(status: string) {
  return status === 'Present'
    ? <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Present</span>
    : <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">Left</span>
}

function csuiteBadge(roles: string[]) {
  if (!roles?.length || roles.length < 2) return null
  return <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-blue-100 text-blue-800 text-[10px] font-bold" title={`Dual role: ${roles.join(', ')}`}>D</span>
}

function csuiteCell(personnel: Person[], manager: string, role: string) {
  const active = personnel.filter(p => p.manager === manager && p.status === 'Present' && p.csuite_roles.includes(role))
  if (!active.length) return <span className="text-red-500">-</span>
  const isDual = active.some(p => p.csuite_roles.length >= 2)
  return (
    <span className="flex items-center justify-center gap-1">
      <span className="text-green-600">&#10003;</span>
      {isDual && <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-blue-100 text-blue-800 text-[9px] font-bold">D</span>}
    </span>
  )
}

function shortMonth(dateStr: string | null) {
  if (!dateStr) return '-'
  try {
    const d = new Date(dateStr)
    return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
  } catch { return dateStr }
}

export default function PersonnelMonitoring() {
  const queryClient = useQueryClient()
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedManager, setSelectedManager] = useState('all')
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid')
  const [expandedManagers, setExpandedManagers] = useState<Record<string, boolean>>({})
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({})
  const [selectedPerson, setSelectedPerson] = useState<Person | null>(null)
  const [showSummary, setShowSummary] = useState(true)
  const [showResignations, setShowResignations] = useState(true)
  const [showJoiners, setShowJoiners] = useState(true)

  const { data, isLoading } = useQuery({
    queryKey: ['personnel-data'],
    queryFn: () => api.get('/personnel/data').then((r) => r.data),
  })

  const { data: cacheStatus } = useQuery({
    queryKey: ['personnel-status'],
    queryFn: () => api.get('/personnel/status').then((r) => r.data),
  })

  const refreshMutation = useMutation({
    mutationFn: () => api.post('/personnel/refresh'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['personnel-data'] })
      queryClient.invalidateQueries({ queryKey: ['personnel-status'] })
    },
  })

  const personnel: Person[] = data?.personnel || []
  const managers: string[] = data?.managers || []
  const coverage: Record<string, CoverageInfo> = data?.coverage || {}

  const stats = useMemo(() => {
    const active = personnel.filter(p => p.status === 'Present')
    return { totalManagers: managers.length, totalPersonnel: active.length, totalLeft: personnel.filter(p => p.status === 'Left').length }
  }, [personnel, managers])

  const cutoff12m = useMemo(() => {
    const d = new Date()
    d.setFullYear(d.getFullYear() - 1)
    return d.toISOString().slice(0, 10)
  }, [])

  const resignations = useMemo(() => {
    return personnel.filter(p => p.status === 'Left' && p.date_left && p.date_left >= cutoff12m)
      .sort((a, b) => (b.date_left || '').localeCompare(a.date_left || ''))
  }, [personnel, cutoff12m])

  const newJoiners = useMemo(() => {
    return personnel.filter(p => p.status === 'Present' && p.date_joined && p.date_joined >= cutoff12m)
      .sort((a, b) => (b.date_joined || '').localeCompare(a.date_joined || ''))
  }, [personnel, cutoff12m])

  const filteredPersonnel = useMemo(() => {
    let result = personnel
    if (selectedManager !== 'all') result = result.filter(p => p.manager === selectedManager)
    if (searchTerm) {
      const q = searchTerm.toLowerCase()
      result = result.filter(p => p.name.toLowerCase().includes(q) || p.position.toLowerCase().includes(q) || p.manager.toLowerCase().includes(q))
    }
    return result
  }, [personnel, selectedManager, searchTerm])

  const personnelByManager = useMemo(() => {
    const map: Record<string, Record<string, Person[]>> = {}
    filteredPersonnel.forEach(p => {
      if (!map[p.manager]) map[p.manager] = {}
      const grp = p.sort_by || 'Other'
      if (!map[p.manager][grp]) map[p.manager][grp] = []
      map[p.manager][grp].push(p)
    })
    return map
  }, [filteredPersonnel])

  const toggleManager = (m: string) => setExpandedManagers(prev => ({ ...prev, [m]: !prev[m] }))
  const toggleGroup = (key: string) => setExpandedGroups(prev => ({ ...prev, [key]: !prev[key] }))

  if (isLoading) return <div className="flex justify-center p-8"><LoadingSpinner /></div>

  if (!personnel.length) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Personnel Monitoring</h1>
            <p className="text-muted-foreground">Investment Manager Personnel Dashboard</p>
          </div>
          <button onClick={() => refreshMutation.mutate()} disabled={refreshMutation.isPending}
            className="h-9 px-4 border border-input rounded-md text-sm font-medium hover:bg-accent flex items-center gap-2 disabled:opacity-50">
            <RefreshCw className={cn('h-4 w-4', refreshMutation.isPending && 'animate-spin')} />
            {refreshMutation.isPending ? 'Refreshing...' : 'Refresh from SFTP'}
          </button>
        </div>
        <div className="bg-card border border-border rounded-lg p-8 text-center text-muted-foreground">
          {data?.message || 'No personnel data available. Click Refresh to download from SFTP.'}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Personnel Monitoring</h1>
          <p className="text-muted-foreground">Investment Manager Personnel Dashboard</p>
        </div>
        <div className="flex items-center gap-3">
          {cacheStatus?.last_updated && <span className="text-xs text-muted-foreground">Updated: {formatDate(cacheStatus.last_updated)}</span>}
          <button onClick={() => refreshMutation.mutate()} disabled={refreshMutation.isPending}
            className="h-9 px-4 border border-input rounded-md text-sm font-medium hover:bg-accent flex items-center gap-2 disabled:opacity-50">
            <RefreshCw className={cn('h-4 w-4', refreshMutation.isPending && 'animate-spin')} />
            {refreshMutation.isPending ? 'Refreshing...' : 'Refresh from SFTP'}
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <MetricCard label="Investment Managers" value={stats.totalManagers} icon={<Building2 className="h-5 w-5" />} />
        <MetricCard label="Active Personnel" value={stats.totalPersonnel} icon={<UserCheck className="h-5 w-5" />} />
        <MetricCard label="Resignations (12M)" value={resignations.length} icon={<UserMinus className="h-5 w-5" />} />
        <MetricCard label="New Joiners (12M)" value={newJoiners.length} icon={<UserPlus className="h-5 w-5" />} />
      </div>

      {/* Executive Coverage Summary */}
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <button className="w-full p-4 border-b border-border flex items-center justify-between hover:bg-muted/50" onClick={() => setShowSummary(!showSummary)}>
          <h2 className="font-semibold">Executive Coverage Summary</h2>
          {showSummary ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </button>
        {showSummary && (
          <div className="overflow-x-auto">
            <table className="data-table text-xs">
              <thead>
                <tr>
                  <th>Investment Manager</th>
                  <th className="text-right">Personnel</th>
                  {CSUITE_ROLES.map(r => <th key={r} className="text-center">{r}</th>)}
                  <th>Main Man</th>
                  <th>Coverage</th>
                  <th>Source</th>
                  <th>Freshness</th>
                </tr>
              </thead>
              <tbody>
                {managers.map(m => {
                  const mPersonnel = personnel.filter(p => p.manager === m && p.status === 'Present')
                  const mainMan = mPersonnel.find(p => p.is_main_man)
                  const cov = coverage[m]
                  return (
                    <tr key={m}>
                      <td className="font-medium whitespace-nowrap">{m}</td>
                      <td className="text-right font-mono">{mPersonnel.length}</td>
                      {CSUITE_ROLES.map(role => <td key={role} className="text-center">{csuiteCell(personnel, m, role)}</td>)}
                      <td className="whitespace-nowrap text-xs">
                        {mainMan ? (
                          <span className="flex items-center gap-1">
                            <Crown className="h-3 w-3 text-purple-600" />
                            {mainMan.name} <span className="text-muted-foreground">({mainMan.position})</span>
                          </span>
                        ) : '-'}
                      </td>
                      <td>{cov ? coverageBadge(cov.coverage) : '-'}</td>
                      <td className="text-xs text-muted-foreground">{cov?.source || '-'}</td>
                      <td className="text-xs text-muted-foreground">{cov?.freshness || '-'}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Resignations */}
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <button className="w-full p-4 border-b border-border flex items-center justify-between hover:bg-muted/50" onClick={() => setShowResignations(!showResignations)}>
          <h2 className="font-semibold">Resignations - Past 12 Months ({resignations.length})</h2>
          {showResignations ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </button>
        {showResignations && (
          <div className="overflow-x-auto">
            {resignations.length ? (
              <table className="data-table text-sm">
                <thead><tr><th>Name</th><th>Position</th><th>Investment Manager</th><th>Date Left</th><th>Tenure</th><th>Team</th></tr></thead>
                <tbody>
                  {resignations.map((p, i) => (
                    <tr key={i} className="cursor-pointer hover:bg-muted/50" onClick={() => setSelectedPerson(p)}>
                      <td className="font-medium">{p.name}</td>
                      <td>{p.position}</td>
                      <td>{p.manager}</td>
                      <td><span className="px-2 py-0.5 rounded text-xs bg-red-100 text-red-800">{shortMonth(p.date_left)}</span></td>
                      <td className="text-muted-foreground text-xs">{p.tenure || '-'}</td>
                      <td>{p.sort_by && p.sort_by !== 'Other' ? <span className="px-2 py-0.5 rounded text-xs bg-blue-100 text-blue-800">{p.sort_by}</span> : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : <div className="p-4 text-muted-foreground text-sm">No resignations in the past 12 months</div>}
          </div>
        )}
      </div>

      {/* New Joiners */}
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <button className="w-full p-4 border-b border-border flex items-center justify-between hover:bg-muted/50" onClick={() => setShowJoiners(!showJoiners)}>
          <h2 className="font-semibold">New Joiners - Past 12 Months ({newJoiners.length})</h2>
          {showJoiners ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </button>
        {showJoiners && (
          <div className="overflow-x-auto">
            {newJoiners.length ? (
              <table className="data-table text-sm">
                <thead><tr><th>Name</th><th>Position</th><th>Investment Manager</th><th>Date Joined</th><th>Tenure</th><th>Team</th></tr></thead>
                <tbody>
                  {newJoiners.map((p, i) => (
                    <tr key={i} className="cursor-pointer hover:bg-muted/50" onClick={() => setSelectedPerson(p)}>
                      <td className="font-medium">{p.name}</td>
                      <td>{p.position}</td>
                      <td>{p.manager}</td>
                      <td><span className="px-2 py-0.5 rounded text-xs bg-blue-100 text-blue-800">{shortMonth(p.date_joined)}</span></td>
                      <td className="text-muted-foreground text-xs">{p.tenure || '-'}</td>
                      <td>{p.sort_by && p.sort_by !== 'Other' ? <span className="px-2 py-0.5 rounded text-xs bg-blue-100 text-blue-800">{p.sort_by}</span> : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : <div className="p-4 text-muted-foreground text-sm">No new joiners in the past 12 months</div>}
          </div>
        )}
      </div>

      {/* Manager Data */}
      <div className="bg-card border border-border rounded-lg p-4">
        <div className="flex items-center gap-4 mb-4 flex-wrap">
          <h2 className="font-semibold">Manager Data</h2>
          <input type="text" placeholder="Search name or position..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
            className="h-8 px-3 border border-input rounded-md text-sm w-60" />
          <select value={selectedManager} onChange={e => setSelectedManager(e.target.value)}
            className="h-8 px-2 border border-input rounded-md text-sm">
            <option value="all">All Managers</option>
            {managers.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
          <div className="flex border border-input rounded-md overflow-hidden">
            <button onClick={() => setViewMode('grid')}
              className={cn('px-2 py-1', viewMode === 'grid' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted')}><Grid3X3 className="h-4 w-4" /></button>
            <button onClick={() => setViewMode('list')}
              className={cn('px-2 py-1', viewMode === 'list' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted')}><List className="h-4 w-4" /></button>
          </div>
        </div>

        {viewMode === 'grid' ? (
          <div className="space-y-3">
            {Object.entries(personnelByManager).sort(([a], [b]) => a.localeCompare(b)).map(([manager, groups]) => {
              const isExp = expandedManagers[manager]
              const totalActive = Object.values(groups).flat().filter(p => p.status === 'Present').length
              const totalPeople = Object.values(groups).flat().length
              return (
                <div key={manager} className="border border-border rounded-lg">
                  <button className="w-full p-3 flex items-center justify-between hover:bg-muted/50 text-left" onClick={() => toggleManager(manager)}>
                    <span className="font-medium">{manager} <span className="text-muted-foreground text-sm">({totalPeople} people, {totalActive} active)</span></span>
                    {isExp ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                  </button>
                  {isExp && (
                    <div className="px-3 pb-3 space-y-2">
                      {Object.entries(groups).sort(([a], [b]) => a.localeCompare(b)).map(([group, people]) => {
                        const gk = `${manager}__${group}`
                        const gExp = expandedGroups[gk] !== false
                        return (
                          <div key={gk}>
                            <button className="w-full p-2 flex items-center gap-2 hover:bg-muted/30 rounded text-sm text-left" onClick={() => toggleGroup(gk)}>
                              {gExp ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                              <span className="font-medium text-muted-foreground">{group}</span>
                              <span className="text-xs text-muted-foreground">({people.length})</span>
                            </button>
                            {gExp && (
                              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2 mt-1 pl-4">
                                {people.map((p, i) => (
                                  <div key={i} onClick={() => setSelectedPerson(p)}
                                    className={cn(
                                      'border rounded-lg p-3 cursor-pointer transition-all hover:shadow-md',
                                      p.status === 'Left' && 'bg-muted/50 opacity-75',
                                      p.csuite_roles.length >= 2 && 'border-yellow-400 bg-yellow-50',
                                      p.is_main_man && 'border-purple-400 border-2 bg-purple-50',
                                    )}>
                                    <div className="flex items-center gap-1 mb-1">
                                      <span className="font-medium text-sm truncate">{p.name}</span>
                                      {p.is_main_man && <Crown className="h-3.5 w-3.5 text-purple-600 flex-shrink-0" />}
                                      {csuiteBadge(p.csuite_roles)}
                                    </div>
                                    <div className="text-xs text-muted-foreground truncate mb-2">{p.position}</div>
                                    <div className="flex flex-wrap gap-1 items-center">
                                      {statusBadge(p.status)}
                                      {p.tenure && <span className="text-[10px] text-muted-foreground">{p.tenure}</span>}
                                    </div>
                                    {p.location && <div className="text-[10px] text-muted-foreground mt-1">{p.location}</div>}
                                    {p.tagging && <span className="inline-block mt-1 px-1.5 py-0.5 rounded text-[10px] bg-blue-100 text-blue-800">{p.tagging}</span>}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table text-sm">
              <thead><tr><th>Name</th><th>Position</th><th>Investment Manager</th><th>Status</th><th>Tenure</th><th>Joined</th><th>Team</th></tr></thead>
              <tbody>
                {filteredPersonnel.map((p, i) => (
                  <tr key={i} className="cursor-pointer hover:bg-muted/50" onClick={() => setSelectedPerson(p)}>
                    <td className="font-medium">
                      <span className="flex items-center gap-1">
                        {p.name}
                        {p.is_main_man && <Crown className="h-3 w-3 text-purple-600" />}
                        {csuiteBadge(p.csuite_roles)}
                      </span>
                    </td>
                    <td>{p.position}</td>
                    <td>{p.manager}</td>
                    <td>{statusBadge(p.status)}</td>
                    <td className="text-xs text-muted-foreground">{p.tenure || '-'}</td>
                    <td className="text-xs">{shortMonth(p.date_joined)}</td>
                    <td>{p.sort_by && p.sort_by !== 'Other' ? <span className="px-1.5 py-0.5 rounded text-xs bg-blue-100 text-blue-800">{p.sort_by}</span> : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Person Detail Modal */}
      {selectedPerson && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setSelectedPerson(null)}>
          <div className="bg-card border border-border rounded-xl shadow-xl p-6 max-w-md w-full mx-4" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold flex items-center gap-2">
                {selectedPerson.name}
                {selectedPerson.is_main_man && <Crown className="h-4 w-4 text-purple-600" />}
              </h3>
              <button onClick={() => setSelectedPerson(null)} className="p-1 hover:bg-muted rounded"><X className="h-4 w-4" /></button>
            </div>
            <div className="space-y-3">
              <div><span className="text-sm text-muted-foreground">Position:</span> <span className="font-medium">{selectedPerson.position}</span></div>
              <div><span className="text-sm text-muted-foreground">Investment Manager:</span> <span className="font-medium">{selectedPerson.manager}</span></div>
              <div><span className="text-sm text-muted-foreground">Status:</span> {statusBadge(selectedPerson.status)}</div>
              {selectedPerson.tenure && <div><span className="text-sm text-muted-foreground">Tenure:</span> <span className="font-medium">{selectedPerson.tenure}</span></div>}
              {selectedPerson.date_joined && <div><span className="text-sm text-muted-foreground">Joined:</span> <span className="font-medium">{shortMonth(selectedPerson.date_joined)}</span></div>}
              {selectedPerson.date_left && <div><span className="text-sm text-muted-foreground">Left:</span> <span className="font-medium">{shortMonth(selectedPerson.date_left)}</span></div>}
              {selectedPerson.location && <div><span className="text-sm text-muted-foreground">Location:</span> <span className="font-medium">{selectedPerson.location}</span></div>}
              {selectedPerson.csuite_roles?.length > 0 && (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">C-Suite:</span>
                  {selectedPerson.csuite_roles.map(r => <span key={r} className="px-2 py-0.5 rounded text-xs bg-blue-100 text-blue-800">{r}</span>)}
                </div>
              )}
              {selectedPerson.sort_by && selectedPerson.sort_by !== 'Other' && (
                <div><span className="text-sm text-muted-foreground">Team:</span> <span className="px-2 py-0.5 rounded text-xs bg-blue-100 text-blue-800">{selectedPerson.sort_by}</span></div>
              )}
              {selectedPerson.tagging && (
                <div><span className="text-sm text-muted-foreground">Tags:</span> <span className="px-2 py-0.5 rounded text-xs bg-blue-100 text-blue-800">{selectedPerson.tagging}</span></div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
